import { createActions } from 'redux-actions';
import { batch } from 'react-redux';
import registrationApi from '../../../api/registrationApi';
import {
  selectAbn,
  selectTrustAbn,
  selectMainPortal,
  selectMainRegistrationMode,
} from '../../../selectors';
// eslint-disable-next-line import/no-cycle
import {
  markCurrentStepAsCompleted,
  markCurrentStepAsCompletedAndGoToNextStep,
  resetCurrentStep,
} from '../steps/actions';
import { showMainLoading, hideMainLoading } from '../main/actions';
import { ACTION_PREFIX, actionTypes } from './constants';
import { getActionOptions } from '../../../utils/reduxactionsutils';

export const { updateTrustValidationResult, resetTrustValidationResult } = createActions(
  actionTypes.UPDATE_TRUST_VALIDATION_RESULT,
  actionTypes.RESET_TRUST_VALIDATION_RESULT,
  getActionOptions(ACTION_PREFIX),
);

const deedUploadStatus = {
  hasError: false,
};

export function checkTrustEligibility(selectedAbn) {
  return (dispatch, getState) => {
    const state = getState();
    const parentAbn = selectAbn(state);
    const portal = selectMainPortal(state);
    const registrationMode = selectMainRegistrationMode(state);

    const setError = (message) => {
      dispatch(
        updateTrustValidationResult({
          abn: selectedAbn,
          isValidated: true,
          isEligible: false,
          message,
        }),
      );
    };

    batch(() => {
      dispatch(showMainLoading('Validating Trust...'));
      dispatch(resetCurrentStep());
    });

    registrationApi
      .validateRelatedEntity(parentAbn.id, selectedAbn.id, portal, registrationMode)
      .then((response) => {
        const { abn, entityDetails, links, isError, errorMessage } = response.data.output;

        if (abn !== selectedAbn.id) {
          return;
        }

        if (isError) {
          setError(errorMessage);
          return;
        }

        if (entityDetails.isValid && entityDetails.isActive) {
          batch(() => {
            dispatch(
              updateTrustValidationResult({
                abn: selectedAbn,
                isValidated: true,
                isEligible: true,
                message: 'This trust is eligible',
              }),
            );

            if (links && links.length) {
              dispatch(markCurrentStepAsCompleted({ nextStepLinks: links }));
            }
          });
        } else {
          setError('Unfortunately, we do not support unregistered businesses at the moment.');
        }
      })
      // eslint-disable-next-line no-unused-vars
      .catch((_reason) => {
        setError('Something went wrong while checking eligibility');
      })
      .finally(() => dispatch(hideMainLoading()));
  };
}

export const uploadDeed = (files, abn, dispatch) => {
  const promises = [];
  Array.prototype.forEach.call(files, (file) => {
    const formData = new FormData();
    formData.append('fileContent', file);
    const promise = registrationApi
      .uploadDeed(abn, formData)
      .then((result) => {
        if (result.data.isUploaded === false) {
          deedUploadStatus.hasError = true;
        }
        return true;
      })
      .catch(() => {
        deedUploadStatus.hasError = true;
        return false;
      })
      .finally(() => dispatch(hideMainLoading()));
    promises.push(promise);
  });
  return promises;
};

export const saveTrustDetails = (values, callback) => (dispatch, getState) => {
  const abn = selectAbn(getState())?.id ?? null;
  const relatedEntityId = selectTrustAbn(getState())?.id ?? null;

  if (!abn || !relatedEntityId) {
    if (callback) {
      callback();
    }
    return;
  }

  const setError = (message) => {
    dispatch(
      updateTrustValidationResult({
        abn: relatedEntityId,
        isValidated: true,
        isEligible: false,
        message,
      }),
    );
  };

  dispatch(showMainLoading('Posting Trust Details...'));

  const requestBody = {
    relatedEntityId,
    deedReferences: '',
  };

  Promise.all(uploadDeed(values.files, abn, dispatch)).then(() => {
    if (deedUploadStatus.hasError === false) {
      if (values.files.length > 1) {
        Array.prototype.forEach.call(values.files, (file) => {
          if (requestBody.deedReferences === '') {
            requestBody.deedReferences = file.name;
          } else {
            requestBody.deedReferences = `${file.name},${requestBody.deedReferences}`;
          }
        });
      } else if (values.files.length === 1) {
        requestBody.deedReferences = values.files[0].name;
      }
      registrationApi
        .saveTrustDetails(abn, requestBody)
        .then(({ data }) => {
          if (callback) {
            callback();
          }
          const {
            output: { isError, errorMessage, links },
            isSuccess,
            messages,
          } = data;

          if (isError) {
            setError(errorMessage);
            return;
          }

          if (!isSuccess) {
            const message =
              messages && messages.length
                ? messages[0]
                : 'An unexpected error has occured while saving the trust details';
            setError(message);
            return;
          }

          if (links && links.length) {
            batch(() => {
              dispatch(resetCurrentStep());
              dispatch(markCurrentStepAsCompletedAndGoToNextStep(links));
            });
          }
        })
        .catch(() => {
          if (callback) {
            callback();
          }
          setError('An unexpected error has occured while posting trust deeds');
        })
        .finally(() => {
          dispatch(hideMainLoading());
        });
    } else {
      setError('An unexpected error has occured while uploading deed files');
      if (callback) {
        callback();
      }
    }
  });
};
